import { PaymentOption } from "../../Payment/PaymentEntities";
import { PhoneToCnpBookingDetails } from "../PhoneToCnpEntities";
import { SampleBookingAccepted } from "../SampleTestData";
import { ErrorMessageType, NoticeType, ToasterType } from "../UI/Notifications/NotificationEntities";
import { BackingUserDetail } from "../../GuestPayment/GuestPaymentState";

/** 
 * Redux store "PhoneToCnp" state slice.
 * Data for phone to CNP booking flow.
 */
export interface PhoneToCnpState {

    /** Various booking details to be displayed in the UI. */
    Booking: PhoneToCnpBookingDetails | null;

    /** Which notice to be displayed in the UI. This is consumed by the Notice component. Null when no notice is displayed. */
    NoticeToDisplay: NoticeType | null;

    /** Which toaster to be displayed in the UI. This is consumed by the Toaster component. Null when no toaster is displayed.  */
    ToasterToDisplay: ToasterType | null;

    /** Which error message to be displayed in the UI. This is consumed by the ErrorMessage component. Null when no error message is displayed. */
    ErrorMessage: ErrorMessageType | null;

    /** Whether the passenger has selected the Fixed Price fare option. When true, the payment section is shown to collect card details. */
    IsFixedFareSelected: boolean;

    /** Whether the passenger has confirmed and completed the Fixed Price payment. When true, the fare selection and payment sections are hidden and the confirmed price section is shown. */
    IsPaymentConfirmed: boolean;

    /** The payment card option selected by the passenger to pay for the Fixed Price fare. Null when no card has been selected yet. */
    PaymentOption: PaymentOption | null;

    /** Whether the booking details are being loaded from the API. When true, a skeleton loader should be displayed. */
    IsLoadingBookingDetails: boolean;

    /** Whether the user wants to save card details for future trips. */
    SaveCardForFutureTrips: boolean;

    /** Which stage of the workflow state for adding a payment method in to a voice booking and converting it to fixed price CNP booking we are currently in. */
    PaymentUpdateState: VoiceBookingUpdateState;

    /**
     * Guest user credentials (from GuestUserAuth returned by the booking load API) for the account that owns the SavedCard.
     * Stored exclusively for the phone-to-CNP flow so that DeleteCard can always authenticate correctly,
     * regardless of what happens to GuestPayment.BackingUser between booking load and card deletion.
     */
    SavedCardGuestAuth: BackingUserDetail | null;
}

/** The workflow states for adding a payment method such as PayPal or Apple Pay in to a voice booking and converting it to fixed price CNP booking. */
export enum VoiceBookingUpdateState {

    /** Not yet requested by the user. i.e. Continue to payment button has not been clicked yet. */
    NotRequested = "NotRequested",

    /** Update is in progress in the background. Disable the button to prevent more clicks and Show a spinner. */
    InProgress = "InProgress",

    /** Finished the API, but with an error. Booking is not updated. */
    FinishedError = "FinishedError",

    /** Finished API calls successfully. Booking is updated. */
    FinishedSuccess = "FinishedSuccess",
}

/** Initial value of the PhoneToCnp Redux state slice. */
export const PhoneToCnpInitialState: PhoneToCnpState = {
    Booking: null,
    NoticeToDisplay: null,
    ToasterToDisplay: null,
    ErrorMessage: null,
    IsFixedFareSelected: false,
    IsPaymentConfirmed: false,
    PaymentOption: null,
    IsLoadingBookingDetails: true,
    SaveCardForFutureTrips: false,
    PaymentUpdateState: VoiceBookingUpdateState.NotRequested,
    SavedCardGuestAuth: null,
}
